import React, { useMemo } from 'react';
import styled, { css } from 'styled-components';

import {
  lima,
  paradigmLightBlue,
  razzmatazz,
} from '@paradigm/design-system/src/colors';
import { hourFormat, shortDateFormat } from '@paradigm/utils/src/date';
import { formatProductQty } from '@paradigm/features/src/unified-rfqs/ui/decimal-format';
import { getProductName } from '@paradigm/features/src/products/domain';
import {
  Drfq,
  getDrfqStatusLabel,
} from '@paradigm/features/src/unified-rfqs/entities/drfq/domain';
import { useDrfqDraftOrHistoryItem } from '@paradigm/features/src/unified-rfqs/contexts/drfqs';
import {
  useDrfqExecutionState,
  useDrfqTradeTapeFetcher,
  useTakerExecutedRfqInfo,
  useTakerOrderbookFetcher,
} from '@paradigm/features/src/unified-rfqs/ui/drfq/hooks';
import { findDrfqBaseLegIndex } from '@paradigm/features/src/unified-rfqs/utils/legs';
import { NEGATIVE_SIGN } from '@paradigm/utils/src/enhanceNegativeSign';

import VirtualizedRow from '#/components/rfq-common/TableRow';
import { QuickActions } from '#/components/UnifiedRfqs/components/QuickActions';
import { MOBILE_BREAKPOINT } from '#/containers/layout/constants';
import BottomTableRow, {
  DesktopRow,
} from '#/components/UnifiedRfqs/BottomTableRow';
import { StrategyDescription } from '#/components/UnifiedRfqs/components/strategy-description/StrategyDescription';
import { useContextMenuActions } from '#/components/UnifiedRfqs/context-menu/context-menu';
import { AnonIconText } from '#/components/UnifiedRfqs/components/WarningMessage';
import { RfqProvider } from '@paradigm/features/src/unified-rfqs/ui/rfq-context';

import {
  Side,
  getOppositeSide,
} from '@paradigm/features/src/unified-rfqs/entities/shared';
import { Maybe } from '@paradigm/utils/types';
import { SpreadFromMarkCellLabel } from '../components/bottom-tabs/SpreadFromMarkCellLabel';
import { SpreadFromMarkColumnLabel } from '../components/bottom-tabs/SpreadFromMarkColumnLabel';

interface Props {
  readonly drfqId: Drfq['id'];
  readonly scrollableRef: React.RefObject<HTMLDivElement>;
  readonly showDrafts: boolean;
}

export default function RfqsHistoryRow(props: Props) {
  const { scrollableRef } = props;
  const { drfqId } = props;

  return (
    <VirtualizedRow
      id={drfqId}
      scrollableRef={scrollableRef}
      rowContent={() => (
        <RfqProvider rfqId={drfqId}>
          <DrfqHistoryRowContent
            drfqId={drfqId}
            showDrafts={props.showDrafts}
          />
        </RfqProvider>
      )}
    />
  );
}

function DrfqHistoryRowContent({
  drfqId,
  showDrafts,
}: {
  drfqId: Drfq['id'];
  showDrafts: boolean;
}) {
  const drfq = useDrfqDraftOrHistoryItem(drfqId, showDrafts);
  if (drfq == null) {
    return null;
  }

  return <RfqsHistoryRowContent drfqId={drfqId} showDrafts={showDrafts} />;
}

export function RfqsHistoryRowContent({
  drfqId,
  showDrafts,
}: {
  drfqId: Drfq['id'];
  showDrafts: boolean;
}) {
  const { open } = useContextMenuActions();
  const drfq = useDrfqDraftOrHistoryItem(drfqId, showDrafts);

  const { isLoading: isLoadingTrade, executedTrade } =
    useDrfqExecutionState(drfq);
  const isExecuted = executedTrade != null;
  const { trade: tradeTape } = useDrfqTradeTapeFetcher(drfq.id, {
    shouldFetch: isExecuted,
  });
  // We invoke this fetcher only to get executedFromDesk (MAKER desk name if not Anon)
  useTakerOrderbookFetcher(drfqId, {
    shouldFetch: isExecuted && drfq.role === 'TAKER',
  });
  const { executedFromDesk } = useTakerExecutedRfqInfo(
    drfqId,
    executedTrade?.side,
  );
  const baseLegIndex = findDrfqBaseLegIndex(drfq.kind, drfq.legs);
  const productCode = drfq.legs[baseLegIndex]?.product_code;
  const qtyFormatter = (value: string | number) =>
    formatProductQty(value, productCode, true);

  const userExecutedSide = useMemo(
    () => getUserExecutedSide(drfq.role, tradeTape?.side),
    [drfq.role, tradeTape?.side],
  );

  return (
    <Row
      onContextMenu={({ x, y }) =>
        open({ rfqId: drfq.id, productCode, anchorPoint: { x, y } })
      }
      columns={[
        {
          id: 'date',
          name: 'Date',
          children: <span>{shortDateFormat(drfq.last_updated_at)}</span>,
        },
        {
          id: 'hour',
          name: 'Hour',
          mobileOptions: { isMainRow: true },
          children: <span>{hourFormat(drfq.last_updated_at)}</span>,
        },
        {
          id: 'status',
          name: 'Status',
          mobileOptions: { isMainRow: true },
          children: (
            <span>
              {isLoadingTrade
                ? NEGATIVE_SIGN
                : getDrfqStatusLabel(drfq, isExecuted, showDrafts)}
            </span>
          ),
        },
        {
          id: 'product',
          name: 'Product',
          children: <span>{getProductName(productCode)}</span>,
        },
        {
          id: 'description',
          name: 'Description',
          mobileOptions: { isMainRow: true },
          children: (
            <StrategyDescription>{drfq.description}</StrategyDescription>
          ),
        },
        {
          id: 'actions',
          name: 'Actions',
          mobileOptions: { hideLabel: true },
          children: (
            <QuickActions
              productCode={productCode}
              baseDDTag="Rfq History Quick Actions"
              rfqId={drfq.id}
              showDrafts={showDrafts}
            />
          ),
        },
        {
          id: 'counterparty',
          name: 'Counterparty',
          mobileOptions: { isHidden: true },
          children: (
            <Counterparty drfq={drfq} executedFromDesk={executedFromDesk} />
          ),
        },
        {
          id: 'quantity',
          name: 'Quantity',
          children: <span>{qtyFormatter(drfq.quantity)}</span>,
        },

        {
          id: 'price',
          name: 'Price',
          children: (
            <span>
              {tradeTape?.displayValues.tradePrice}
              <SpreadFromMarkCellLabel
                value={tradeTape?.displayValues.markOffset}
              />
            </span>
          ),
          mobileOptions: {
            secondaryLabelContent: <SpreadFromMarkColumnLabel />,
          },
        },
        {
          id: 'side',
          name: 'Side',
          children: <ActionLabel>{userExecutedSide}</ActionLabel>,
        },
        {
          id: 'role',
          name: 'Role',
          children: <span>{drfq.role === 'MAKER' ? 'Maker' : 'Taker'}</span>,
        },
        {
          id: 'account',
          name: 'Account',
          children: <span>{drfq.account_name ?? NEGATIVE_SIGN}</span>,
        },
      ]}
    />
  );
}

function Counterparty({
  drfq,
  executedFromDesk,
}: {
  drfq: Drfq;
  executedFromDesk: string | null;
}) {
  const counterparty =
    drfq.role === 'MAKER' ? drfq.taker_desk_name : executedFromDesk;
  return drfq.role === 'MAKER' && drfq.is_taker_anonymous ? (
    <AnonIconText color="white" opacity={0.6} />
  ) : (
    <div className="d-flex justify-content-center">
      {counterparty ?? NEGATIVE_SIGN}
    </div>
  );
}

export function RfqsHistoryHeaderRow() {
  return (
    <HeaderRow>
      <span>Date</span>
      <span>Time</span>
      <span>Status</span>
      <span>Product</span>
      <span>Description</span>
      <span>Actions</span>
      <span>Counterparty</span>
      <span>Qty</span>
      <span>
        Price
        <SpreadFromMarkColumnLabel />
      </span>
      <span>Side</span>
      <span>Role</span>
      <span>Account</span>
    </HeaderRow>
  );
}

const COLUMNS_CSS = css`
  grid-template-columns: 68px 50px 80px 120px 250px 115px 84px 80px 80px 80px 80px 200px;
  grid-template-areas: 'date time status prod desc actions counterparty qty pric side role account';
`;

const Row = styled(BottomTableRow)`
  ${COLUMNS_CSS}

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    .mobile-bottom-table-row {
      display: grid;
      font-size: 0.875rem;
      grid-template-columns: 30px 60px auto;
    }
  }
`;

const HeaderRow = styled(DesktopRow)`
  ${COLUMNS_CSS}
  position: sticky;
  top: 0;
  z-index: 1;
  color: ${paradigmLightBlue};

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    display: none;
  }
`;

const ActionLabel = styled.span`
  font-size: 14px;
  ${(props) => props.children === 'SELL' && `color: ${razzmatazz};`}
  ${(props) => props.children === 'BUY' && `color: ${lima};`}
`;

const getUserExecutedSide = (
  userRole: Drfq['role'],
  tradeTapeSide: Maybe<Side>,
) => {
  if (tradeTapeSide == null) return null;
  if (userRole === 'TAKER') return tradeTapeSide;

  // if user is MAKER, the side is the opposite of the trade tape
  return getOppositeSide(tradeTapeSide);
};
